'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowLeft, Cloud, Server, Shield, Zap, CheckCircle, Code, BarChart3, Globe, Database, Lock, Cpu } from "lucide-react";
import { useParams } from "next/navigation";
import { localizeSpelling } from "@/lib/spelling-localization";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const cloudComputingData = {
  title: "Cloud Computing",
  subtitle: "Scalable, secure, and cost-effective cloud infrastructure solutions that enable businesses to innovate faster and scale efficiently",
  headerGradient: "bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500",
  accentColor: "text-blue-600",
  buttonColor: "bg-blue-600 hover:bg-blue-700",
  
  overview: "Cloud Computing provides on-demand access to computing resources, storage, and services over the internet. Our cloud solutions help businesses reduce infrastructure costs, improve scalability, enhance security, and accelerate innovation by leveraging the power of distributed computing platforms.",
  
  capabilities: [
    {
      name: "Infrastructure as a Service (IaaS)",
      description: "Virtualized computing resources including servers, storage, and networking",
      icon: Server,
      applications: ["Virtual Machines", "Block Storage", "Load Balancers", "Virtual Networks"],
      useCases: ["Web hosting", "Development environments", "Disaster recovery", "High-performance computing"]
    },
    {
      name: "Platform as a Service (PaaS)",
      description: "Complete development and deployment environment in the cloud",
      icon: Code,
      applications: ["App Services", "Container Platforms", "Database Services", "API Management"],
      useCases: ["Application development", "Microservices", "API hosting", "DevOps pipelines"]
    },
    {
      name: "Software as a Service (SaaS)",
      description: "Ready-to-use software applications delivered over the internet",
      icon: Globe,
      applications: ["CRM Systems", "Email Services", "Collaboration Tools", "Analytics Platforms"],
      useCases: ["Business applications", "Productivity suites", "Customer management", "Data analytics"]
    }
  ],
  
  technologies: [
    {
      category: "Cloud Providers",
      tools: ["Amazon AWS", "Microsoft Azure", "Google Cloud Platform", "IBM Cloud", "Oracle Cloud", "Alibaba Cloud"],
      description: "Leading cloud infrastructure and platform providers"
    },
    {
      category: "Container Technologies",
      tools: ["Docker", "Kubernetes", "OpenShift", "Amazon ECS", "Azure Container Instances", "Google GKE"],
      description: "Containerization and orchestration platforms"
    },
    {
      category: "Serverless Computing",
      tools: ["AWS Lambda", "Azure Functions", "Google Cloud Functions", "Vercel", "Netlify Functions", "Cloudflare Workers"],
      description: "Event-driven, serverless compute services"
    },
    {
      category: "Database Services",
      tools: ["Amazon RDS", "Azure SQL Database", "Google Cloud SQL", "DynamoDB", "Cosmos DB", "Firebase"],
      description: "Managed database services and NoSQL solutions"
    },
    {
      category: "DevOps & CI/CD",
      tools: ["AWS CodePipeline", "Azure DevOps", "Google Cloud Build", "Jenkins", "GitLab CI", "GitHub Actions"],
      description: "Continuous integration and deployment tools"
    },
    {
      category: "Monitoring & Security",
      tools: ["CloudWatch", "Azure Monitor", "Google Cloud Monitoring", "Datadog", "New Relic", "AWS Security Hub"],
      description: "Cloud monitoring, logging, and security services"
    }
  ],
  
  industries: [
    {
      name: "Financial Services",
      applications: ["Core banking systems", "Trading platforms", "Risk management", "Regulatory compliance"],
      impact: "60% reduction in infrastructure costs, 99.99% uptime for critical systems"
    },
    {
      name: "Healthcare",
      applications: ["Electronic health records", "Telemedicine platforms", "Medical imaging", "Clinical research"],
      impact: "50% faster deployment of new services, HIPAA-compliant infrastructure"
    },
    {
      name: "E-commerce & Retail",
      applications: ["Online stores", "Inventory management", "Customer analytics", "Payment processing"],
      impact: "300% traffic handling during peak seasons, 40% cost savings"
    },
    {
      name: "Education",
      applications: ["Learning management systems", "Virtual classrooms", "Student information systems", "Research computing"],
      impact: "Support for 10x more concurrent users, 70% reduction in IT overhead"
    },
    {
      name: "Manufacturing",
      applications: ["IoT data processing", "Supply chain management", "Quality control systems", "Predictive maintenance"],
      impact: "Real-time processing of millions of sensor readings, 45% improvement in efficiency"
    },
    {
      name: "Media & Entertainment",
      applications: ["Content delivery", "Video streaming", "Digital asset management", "Live broadcasting"],
      impact: "Global content delivery with 99.9% availability, 60% faster content processing"
    }
  ],
  
  serviceModels: [
    {
      model: "Infrastructure as a Service (IaaS)",
      description: "Fundamental computing resources delivered as a service",
      examples: ["Virtual machines", "Storage systems", "Network infrastructure", "Load balancers"],
      benefits: ["Complete control over infrastructure", "Pay-as-you-use pricing", "Rapid scaling", "No hardware maintenance"]
    },
    {
      model: "Platform as a Service (PaaS)",
      description: "Complete development and deployment environment",
      examples: ["Application hosting", "Database services", "Development tools", "Runtime environments"],
      benefits: ["Faster development cycles", "Built-in scalability", "Integrated services", "Reduced complexity"]
    },
    {
      model: "Software as a Service (SaaS)",
      description: "Ready-to-use applications accessible via web browsers",
      examples: ["CRM applications", "Email services", "Office suites", "Analytics tools"],
      benefits: ["No installation required", "Automatic updates", "Anywhere access", "Subscription-based pricing"]
    }
  ],
  
  process: [
    {
      step: "1",
      title: "Cloud Strategy & Assessment",
      description: "Evaluate current infrastructure and define cloud migration strategy",
      details: ["Infrastructure audit", "Cost-benefit analysis", "Security assessment", "Migration roadmap planning"]
    },
    {
      step: "2", 
      title: "Architecture Design",
      description: "Design scalable and secure cloud architecture tailored to business needs",
      details: ["Multi-tier architecture", "Security framework", "Disaster recovery planning", "Performance optimization"]
    },
    {
      step: "3",
      title: "Migration & Implementation",
      description: "Execute cloud migration with minimal downtime and risk",
      details: ["Phased migration approach", "Data migration", "Application modernization", "Testing and validation"]
    },
    {
      step: "4",
      title: "Optimization & Management",
      description: "Continuously optimize performance, costs, and security",
      details: ["Performance monitoring", "Cost optimization", "Security management", "Automated scaling"]
    }
  ],
  
  benefits: [
    "Reduce infrastructure costs by up to 60%",
    "Scale resources instantly based on demand", 
    "Improve system reliability and uptime",
    "Enhance security with enterprise-grade protection",
    "Enable remote work and collaboration",
    "Accelerate innovation and time-to-market",
    "Ensure business continuity and disaster recovery",
    "Access latest technologies without large investments"
  ],
  
  challenges: [
    {
      challenge: "Security & Compliance",
      solution: "Multi-layered security architecture and compliance frameworks",
      description: "Implement robust security controls and meet regulatory requirements"
    },
    {
      challenge: "Data Migration",
      solution: "Phased migration strategy with minimal downtime",
      description: "Seamlessly transfer data and applications to the cloud"
    },
    {
      challenge: "Cost Management",
      solution: "Automated cost monitoring and optimization tools",
      description: "Control and optimize cloud spending across all services"
    },
    {
      challenge: "Vendor Lock-in",
      solution: "Multi-cloud and hybrid cloud strategies",
      description: "Maintain flexibility and avoid dependency on single providers"
    }
  ]
};

export default function CloudComputingPage() {
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  
  // Function to localize text
  const localize = (text: string) => localizeSpelling(text, currentLocale);
  
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section */}
      <header className={`py-20 md:py-28 text-white ${cloudComputingData.headerGradient} relative overflow-hidden`}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, white 2px, transparent 2px),
                             radial-gradient(circle at 75% 75%, white 2px, transparent 2px),
                             radial-gradient(circle at 50% 50%, white 1px, transparent 1px)`,
            backgroundSize: '50px 50px, 60px 60px, 30px 30px'
          }}></div>
        </div>
        
        <div className="container mx-auto max-w-6xl px-4 relative z-10">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Link href={`/${currentLocale}`} className="inline-flex items-center text-sm text-white/80 hover:text-white transition-colors duration-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <ArrowLeft size={18} className="mr-2" />
              {localize("Back to Homepage")}
            </Link>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
                {localize(cloudComputingData.title)}
              </h1>
              <p className="text-xl text-cyan-100 mb-8 leading-relaxed">
                {localize(cloudComputingData.subtitle)}
              </p>
              <div className="flex flex-wrap gap-3">
                {["AWS", "Azure", "Kubernetes", "Serverless"].map((tag, index) => (
                  <Badge key={tag} variant="secondary" className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                    {localize(tag)}
                  </Badge>
                ))}
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <Cloud className="w-12 h-12 mx-auto mb-3 text-cyan-200" />
                    <div className="text-2xl font-bold">99.99%</div>
                    <div className="text-sm text-cyan-100">{localize("Uptime SLA")}</div>
                  </div>
                  <div className="text-center">
                    <Zap className="w-12 h-12 mx-auto mb-3 text-cyan-200" />
                    <div className="text-2xl font-bold">60%</div>
                    <div className="text-sm text-cyan-100">{localize("Cost Reduction")}</div>
                  </div>
                  <div className="text-center">
                    <Shield className="w-12 h-12 mx-auto mb-3 text-cyan-200" />
                    <div className="text-2xl font-bold">24/7</div>
                    <div className="text-sm text-cyan-100">{localize("Security Monitoring")}</div>
                  </div>
                  <div className="text-center">
                    <BarChart3 className="w-12 h-12 mx-auto mb-3 text-cyan-200" />
                    <div className="text-2xl font-bold">Auto</div>
                    <div className="text-sm text-cyan-100">{localize("Scaling")}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto max-w-6xl px-4 py-16">
        
        {/* Overview Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-200">
            <h2 className={`text-3xl font-bold mb-6 ${cloudComputingData.accentColor}`}>
              {localize("What is Cloud Computing?")}
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed">
              {localize(cloudComputingData.overview)}
            </p>
          </div>
        </motion.section>

        {/* Core Capabilities */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Cloud Service Models")}
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {cloudComputingData.capabilities.map((capability, index) => (
              <motion.div key={capability.name} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-l-4 border-l-blue-500">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <capability.icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <CardTitle className="text-xl">{localize(capability.name)}</CardTitle>
                    </div>
                    <CardDescription className="text-base">
                      {localize(capability.description)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Services")}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {capability.applications.map((app) => (
                            <Badge key={app} variant="outline" className="text-xs">
                              {localize(app)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Use Cases")}
                        </h4>
                        <ul className="space-y-1">
                          {capability.useCases.map((useCase) => (
                            <li key={useCase} className="text-sm text-slate-600 flex items-start">
                              <CheckCircle className="w-3 h-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              {localize(useCase)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Service Models Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Cloud Service Models Explained")}
          </motion.h2>
          
          <div className="space-y-8">
            {cloudComputingData.serviceModels.map((model, index) => (
              <motion.div key={model.model} variants={itemVariants}>
                <Card className="hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-xl flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      {localize(model.model)}
                    </CardTitle>
                    <CardDescription className="text-base">{localize(model.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-3">
                          {localize("Examples")}
                        </h4>
                        <ul className="space-y-2">
                          {model.examples.map((example) => (
                            <li key={example} className="text-sm text-slate-600 flex items-start">
                              <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-2 mt-2 flex-shrink-0"></div>
                              {localize(example)}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-3">
                          {localize("Key Benefits")}
                        </h4>
                        <ul className="space-y-2">
                          {model.benefits.map((benefit) => (
                            <li key={benefit} className="text-sm text-slate-600 flex items-start">
                              <CheckCircle className="w-3 h-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              {localize(benefit)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Technology Stack */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-12">
            {localize("Cloud Technology Stack")}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cloudComputingData.technologies.map((tech, index) => (
              <Card key={tech.category} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Code className="w-5 h-5 text-blue-600" />
                    {localize(tech.category)}
                  </CardTitle>
                  <CardDescription>{localize(tech.description)}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {tech.tools.map((tool) => (
                      <Badge key={tool} variant="secondary" className="text-xs">
                        {tool}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>

        {/* Cloud Challenges */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Cloud Computing Challenges & Solutions")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {cloudComputingData.challenges.map((item, index) => (
              <motion.div key={item.challenge} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg text-red-600">{localize(item.challenge)}</CardTitle>
                    <CardDescription className="text-base">{localize(item.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <h4 className="font-semibold text-green-800 mb-2">{localize("Our Solution")}</h4>
                      <p className="text-sm text-green-700">{localize(item.solution)}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Industry Applications */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Industry Applications")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cloudComputingData.industries.map((industry, index) => (
              <motion.div key={industry.name} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg">{localize(industry.name)}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Applications")}
                        </h4>
                        <ul className="space-y-1">
                          {industry.applications.map((app) => (
                            <li key={app} className="text-sm text-slate-600 flex items-start">
                              <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-2 mt-2 flex-shrink-0"></div>
                              {localize(app)}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="pt-3 border-t border-slate-200">
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Impact")}
                        </h4>
                        <p className="text-sm text-green-700 font-medium">{localize(industry.impact)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Our Process */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-12">
            {localize("Our Cloud Implementation Process")}
          </h2>
          
          <div className="space-y-8">
            {cloudComputingData.process.map((step, index) => (
              <div key={step.step} className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {step.step}
                  </div>
                </div>
                <div className="flex-1">
                  <Card className="hover:shadow-lg transition-shadow duration-300">
                    <CardHeader>
                      <CardTitle className="text-xl">{localize(step.title)}</CardTitle>
                      <CardDescription className="text-base">{localize(step.description)}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="grid md:grid-cols-2 gap-2">
                        {step.details.map((detail) => (
                          <li key={detail} className="text-sm text-slate-600 flex items-start">
                            <CheckCircle className="w-3 h-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            {localize(detail)}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Benefits */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 md:p-12 border border-blue-200">
            <h2 className="text-3xl font-bold text-center mb-8">
              {localize("Why Choose Cloud Computing?")}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {cloudComputingData.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-slate-700">{localize(benefit)}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="text-center"
        >
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-200">
            <h2 className="text-3xl font-bold mb-4">
              {localize("Ready to Transform Your Business with Cloud Computing?")}
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              {localize("Let our cloud experts help you migrate to the cloud and build scalable, secure, and cost-effective infrastructure solutions.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                href={`/${currentLocale}/contact`}
                className={`${cloudComputingData.buttonColor} text-white font-semibold py-3 px-8 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105`}
              >
                {localize("Start Your Cloud Journey")}
              </Link>
              <Link 
                href={`/${currentLocale}/services`}
                className="bg-slate-100 text-slate-700 font-semibold py-3 px-8 rounded-lg text-lg hover:bg-slate-200 transition-colors duration-300"
              >
                {localize("Explore Our Services")}
              </Link>
            </div>
          </div>
        </motion.section>
      </main>
    </div>
  );
} 